import React from 'react';
import { Search, Sliders, GraduationCap, Rocket, HeadphonesIcon, Sparkles, Target, Zap } from 'lucide-react';

const AboutPage = () => {
  const implementationSteps = [
    {
      icon: Search,
      title: 'Discovery & Onboarding',
      description: 'We work closely with your team to identify key pain points and map inadequate resource distribution.'
    },
    {
      icon: Sliders,
      title: 'Strategic Customization',
      description: 'We tailor our IT solutions to fit the specific demographic and geographic requirements of your constituency.'
    },
    {
      icon: GraduationCap,
      title: 'Comprehensive Training',
      description: 'We conduct hands-on training sessions for your staff on all dashboards (Agent, Support, Client).'
    },
    {
      icon: Rocket,
      title: 'Seamless Deployment',
      description: 'We roll out the solutions and ensure they are integrated into your daily operations for immediate value.'
    },
    {
      icon: HeadphonesIcon,
      title: 'Ongoing Support',
      description: 'We offer continuous technical and operational support to ensure long-term success and evolving voter engagement.'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Data-Driven',
      description: 'Every solution is backed by real-time data and analytics, enabling informed decision-making for better governance.'
    },
    {
      icon: Zap,
      title: 'Community-Focused',
      description: 'We prioritize the needs of rural communities, ensuring technology serves the people, not the other way around.'
    },
    {
      icon: Sparkles,
      title: 'Empowerment-Led',
      description: 'By employing and training local women agents, we create sustainable employment while building trust.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 container-custom text-center">
          <div className="inline-block bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
            About AEPL
          </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            Partnering with Leadership<br />to <span className="text-cyan-400">Serve Society</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Built on trust, powered by innovation, driven by impact
          </p>
        </div>
      </section>

      {/* Mission & Philosophy */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="mb-16">
              <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
                Our Mission
              </div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Fundamentally Transforming Public Service
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                To fundamentally alter and upgrade the way Public Servants connect with their constituents by leveraging technology to build stronger and more responsive relationships.
              </p>
            </div>
            
            <div className="mb-16">
              <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
                Our Philosophy
              </div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Technology Should Serve Society
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-6">
                We believe technology should serve society. For us, technology is a partner to leadership, helping public servants turn complex challenges into real solutions.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Founded as <strong className="text-gray-900">AEPL (Amical Enterprises Pvt Ltd)</strong>, we created <strong className="text-cyan-600">Gramtek</strong> as a dedicated brand to enhance the rural technology experience. Our solutions are built on the foundation of Voter Relationship Management (VRM), enabling elected representatives to maintain meaningful, data-driven connections with their communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-white mesh-gradient">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Process
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              A Partnership, Not Just a Product
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Five strategic steps to transformation
            </p>
          </div>

          {/* Timeline */}
          <div className="max-w-5xl mx-auto space-y-8">
            {implementationSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="flex gap-6 items-start group">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    {index < implementationSteps.length - 1 && (
                      <div className="w-0.5 h-20 bg-gradient-to-b from-cyan-200 to-transparent mt-4" />
                    )}
                  </div>
                  <div className="flex-1 pt-3">
                    <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all border border-gray-100">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-cyan-500 font-bold text-lg">Step {index + 1}</span>
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-gray-900">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Core Values
            </div>
            <h2 className="text-5xl font-bold mb-6 text-gray-900">
              The AEPL Difference
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="modern-card text-center group">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;